import React, { useState } from "react";

import TopInfoBar from "./TopInfoBar.jsx";
import Instructions from "./Instructions.jsx";
import Table from "../../shared/bootstrap/PaginatedTable/Table.jsx";
import PaginationBar from "../../shared/bootstrap/PaginatedTable/PaginationBar.jsx";
import OrderEntry from "./OrderEntry.jsx";
import TableHeaderComponent from "../../shared/TableHeaderColumn.jsx";

import { generateSort, ordersListSortMap } from "../../../util/sort.js";

import {
  ORDER_APP_PAGE_SIZE,
  SORT_STN_DESC,
  SORT_COMPANY_DESC,
  SORT_ORDER_NUMBER_DESC,
  SORT_RFQ_DESC,
  SORT_AWARD_DATE_DESC,
  SORT_DELIVERY_DATE_DESC,
  SORT_ORDER_TOTAL_DESC,
  SORT_ORDER_STATUS_DESC,
} from "../../../constants.js";

/**
 * OrdersList Component
 * @param {Object} props
 * @param {OrdersListItem[]} props.ordersList
 * @param {{firstName: string, lastName: string}} props.user
 * @param {boolean} props.loadingOrdersList
 * @returns {React.JSX.Element}
 */
export default function OrdersList({ ordersList, user, loadingOrdersList }) {
  const [displayCount, setDisplayCount] = useState(ORDER_APP_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOption, setSortOption] = useState(SORT_STN_DESC);

  const totalPages = ordersList.length === 0 ? 1 : Math.ceil(ordersList.length / displayCount);

  const changeDisplay = () => {
    displayCount === ORDER_APP_PAGE_SIZE ? setDisplayCount(ordersList.length) : setDisplayCount(ORDER_APP_PAGE_SIZE);
    setCurrentPage(0);
  };

  /**
   * Maps OrdersListItem object to Component
   * @param {OrdersListItem} item
   * @returns {React.JSX.Element}
   */
  const OrdersListItemDisplay = (item) => <OrderEntry key={item.stnSeq} item={item} user={user}/>;

  const sliceData = (data) => data.slice(currentPage * displayCount, (currentPage + 1) * displayCount);
  const sortData = (data) => data.sort(ordersListSortMap[sortOption]);

  const displayData = sliceData(sortData(ordersList));

  return (<>
    <TopInfoBar
      user={user}
      ordersCount={ordersList.length}
      displayCount={displayCount}
      changeDisplay={changeDisplay}
      currentPageCount={displayData.length}
    />
    <Instructions ordersCount={ordersList.length}/>
    <Table
      id="orderlist"
      data={displayData}
      mapper={OrdersListItemDisplay}
      caption="Sorted list of orders"
      loading={loadingOrdersList}
      loadingMessage="Loading Orders..."
      emptyMessage="No orders to display."
    >
      <thead role="rowgroup">
      <tr role="row" className="order-entry-row">
        <TableHeaderComponent
          id="stnseq-head"
          className="text-center"
          text="SEWP Tracking Number (STN)"
          onClick={generateSort(SORT_STN_DESC, setSortOption)}
          title="STN"
          sortOption={sortOption}
          sortDesc={SORT_STN_DESC}
        />
        <TableHeaderComponent
          id="order-head"
          className="text-center"
          text="Order Number"
          onClick={generateSort(SORT_ORDER_NUMBER_DESC, setSortOption)}
          title="order number"
          sortOption={sortOption}
          sortDesc={SORT_ORDER_NUMBER_DESC}
        />
        <TableHeaderComponent
          id="rfq-head"
          className="text-center"
          text="RFQ"
          onClick={generateSort(SORT_RFQ_DESC, setSortOption)}
          title="RFQ"
          sortOption={sortOption}
          sortDesc={SORT_RFQ_DESC}
        />
        <TableHeaderComponent
          id="ch-head"
          className="text-center"
          text="Contract Holder (CH)"
          onClick={generateSort(SORT_COMPANY_DESC, setSortOption)}
          title="CH"
          sortOption={sortOption}
          sortDesc={SORT_COMPANY_DESC}
        />
        <TableHeaderComponent
          id="awarddate-head"
          className="text-center"
          text="Award Date"
          onClick={generateSort(SORT_AWARD_DATE_DESC, setSortOption)}
          title="award date"
          sortOption={sortOption}
          sortDesc={SORT_AWARD_DATE_DESC}
        />
        <TableHeaderComponent
          id="expecteddeliverydate-head"
          className="text-center"
          text="Expected Delivery Date"
          onClick={generateSort(SORT_DELIVERY_DATE_DESC, setSortOption)}
          title="expected delivery date"
          sortOption={sortOption}
          sortDesc={SORT_DELIVERY_DATE_DESC}
        />
        <TableHeaderComponent
          id="ordertotal-head"
          className="text-center"
          text="Order Total"
          onClick={generateSort(SORT_ORDER_TOTAL_DESC, setSortOption)}
          title="order total"
          sortOption={sortOption}
          sortDesc={SORT_ORDER_TOTAL_DESC}
        />
        <TableHeaderComponent
          id="orderstatus-head"
          className="text-center"
          text="Status"
          onClick={generateSort(SORT_ORDER_STATUS_DESC, setSortOption)}
          title="status"
          sortOption={sortOption}
          sortDesc={SORT_ORDER_STATUS_DESC}
        />
        <TableHeaderComponent id="emaillink-head" className="text-center" title="Not sortable" text="Send E-mail"/>
      </tr>
      </thead>
    </Table>
    <PaginationBar
      id="pagination-box"
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      totalPages={totalPages}
    />
  </>);
}
